import { Fragment, cloneElement, useMemo, useRef, ReactElement } from 'react';
import { assert } from 'zf-infra';
import { Mode, join } from 'light-component';
import { THead } from './head';
import { TBody } from './body';
import { service } from './service';
import styles from './style.module.less';
import type { CellPos, GridDesignerModalRef, GridDesignerProps, Action, CellSetting } from './types';


/**
 * 表格属性编辑器，支持多个行/列的标题
 *
 * - 表格的设置信息保存在option中
 *   data.option = [
 *     [{title: 'A'}, {title: 'B', width: '200px'}]
 *   ]
 *
 * - 表格的内容保存在value中
 *   data.value = [
 *     ['a', 'b']
 *   ]
 *
 * 以下代码拷贝自旧 web-sdk 工程的 src/form/widget/grid.js
 */
export function GridDesigner(props: GridDesignerProps): ReactElement {
  const modalRef = useRef<GridDesignerModalRef<CellSetting> | null>(null);
  const cellModalRef = useRef<GridDesignerModalRef<CellSetting> | null>(null);

  const data = useMemo(() => service.compose(props), [props]);

  async function onChange(action: Action, cell: CellPos, value?: CellSetting, rows?: CellSetting[][]): Promise<void> {
    // 设置单元格/行/列属性
    if (['setting-col', 'setting-row'].includes(action)) {
      assert(value != null, 'value is required');
      const changed = await modalRef.current?.show(value, cell);
      if (changed == null) {
        return;
      }

      const values = service.getChangedOption(data, cell, changed);
      const result = service.decompose(values, props.gridColTitle, props.gridRowTitle);
      return props.onChange?.(result);
    }

    if (action === 'setting-cell') {
      assert(value != null, 'value is required');
      const changed = await cellModalRef.current?.show(value, cell);
      if (changed == null) {
        return;
      }

      const values = service.getChangedOption(data, cell, changed);
      const result = service.decompose(values, props.gridColTitle, props.gridRowTitle);
      return props.onChange?.(result);
    }

    if (action === 'change-all' && rows != null) {
      const result = service.decompose(rows, props.gridColTitle, props.gridRowTitle);
      return props.onChange?.(result);
    }

    // 添加删除行列，修改单元格内容
    const values = service.getChangedValues(data, action, cell, value);
    const result = service.decompose(values, props.gridColTitle, props.gridRowTitle);
    props.onChange?.(result);
  }

  return (
    <Fragment>
      <table className={join(styles.grid, props.className)}>
        <THead
          mode={props.mode ?? Mode.DESIGN}
          type={props.type}
          option={data}
          cols={props.gridCol ?? 1}
          onChange={onChange}
        />
        <TBody
          data={data}
          type={props.type}
          mode={props.mode ?? Mode.DESIGN}
          rows={props.gridRow ?? 1}
          gridRowTitle={props.gridRowTitle ?? 0}
          gridColTitle={props.gridColTitle ?? 0}
          onChange={onChange}
        />
      </table>
      {props.settings != null && cloneElement(props.settings, { ref: modalRef })}
      {props.cellModal != null && cloneElement(props.cellModal, { ref: cellModalRef })}
    </Fragment>
  );
}
